import { useState, useEffect, useMemo } from "react";
import { Activity, BarChart3, Mail, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "components/ui/dialog";
import { Button } from "components/ui/button";
import CampaignStats from "components/dashboard/dashboard-data/campaign-stats/CampaignStats";
import CampaignGraph from "components/dashboard/dashboard-data/campaign-stats-graph/CampaignStatsGraph";

const STATUS_MAP = {
    active: { dot: "bg-emerald-500", bar: "from-emerald-400 to-teal-500", badge: "bg-emerald-100 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-400" },
    progress: { dot: "bg-blue-500", bar: "from-blue-500 to-indigo-500", badge: "bg-blue-100 dark:bg-blue-900/25 text-blue-700 dark:text-blue-400" },
    pc: { dot: "bg-amber-500", bar: "from-amber-400 to-orange-500", badge: "bg-amber-100 dark:bg-amber-900/25 text-amber-700 dark:text-amber-400" },
    "partially completed": { dot: "bg-amber-500", bar: "from-amber-400 to-orange-500", badge: "bg-amber-100 dark:bg-amber-900/25 text-amber-700 dark:text-amber-400" },
    completed: { dot: "bg-violet-500", bar: "from-violet-500 to-purple-500", badge: "bg-violet-100 dark:bg-violet-900/25 text-violet-700 dark:text-violet-400" },
    terminated: { dot: "bg-red-500", bar: "from-red-400 to-rose-500", badge: "bg-red-100 dark:bg-red-900/25 text-red-700 dark:text-red-400" },
};

export const getStatusStyle = (status) =>
    STATUS_MAP[status?.toLowerCase()] || {
        dot: "bg-gray-400", bar: "from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500",
        badge: "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400",
    };

export default function CampaignStatsDialog({ campaign, onClose }) {
    const [showGraph, setShowGraph] = useState(false);
    useEffect(() => { setShowGraph(false); }, [campaign?.id]);

    const statsDetails = useMemo(() => {
        if (!campaign) return null;
        return {
            id: campaign.id,
            name: campaign.name || "Untitled Campaign",
            temp_name: campaign.temp_name || null,
            temp_id: campaign.temp_id || null,
            c_grp: campaign.c_grp || null,
            uuid: campaign.uuid || campaign.id,
            s_date: campaign.s_date || campaign.startDate || "—",
            e_date: campaign.e_date || campaign.endDate || "—",
            c_status: campaign.c_status || campaign.status || "—",
            email_used: campaign.email_used || "—",
        };
    }, [campaign]);

    if (!campaign || !statsDetails) return null;

    const statusStyle = getStatusStyle(campaign.c_status || campaign.status);

    return (
        <Dialog open={!!campaign} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent size="xl" padding={false} showClose={false}>
                {/* ── Header ── */}
                <DialogHeader className="relative px-6 pt-6 pb-4 border-b border-line dark:border-dark-700 flex-shrink-0">
                    {/* Accent bar */}
                    <div className={`absolute top-0 inset-x-0 h-[2px] rounded-t-2xl bg-gradient-to-r ${statusStyle.bar}`} />

                    <div className="flex items-center justify-between gap-4">
                        {/* Left: icon + title */}
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                                <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2} />
                            </div>
                            <div className="min-w-0">
                                <DialogTitle className="text-title font-bold text-content dark:text-white leading-tight truncate">
                                    {campaign.name || "Campaign Stats"}
                                </DialogTitle>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide border ${statusStyle.badge} border-current/20`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                                        {campaign.c_status || campaign.status || "—"}
                                    </span>
                                    {(campaign.email_used) && (
                                        <span className="flex items-center gap-1 text-meta font-medium text-content-muted dark:text-gray-400 truncate">
                                            <Mail className="w-3 h-3 flex-shrink-0" />
                                            {campaign.email_used}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right: toggle + close */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                                variant="primary"
                                onClick={() => setShowGraph(p => !p)}
                                className="h-8 px-3 text-[12px]"
                            >
                                {showGraph ? (
                                    <>
                                        <BarChart3 className="w-3.5 h-3.5" />
                                        View Stats
                                    </>
                                ) : (
                                    <>
                                        <Activity className="w-3.5 h-3.5" />
                                        View Graph
                                    </>
                                )}
                            </Button>
                            <DialogClose asChild>
                                <button
                                    onClick={onClose}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg
                                        text-content-subtle hover:text-content-secondary dark:text-gray-500 dark:hover:text-gray-300
                                        hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors duration-150"
                                    aria-label="Close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </DialogClose>
                        </div>
                    </div>
                </DialogHeader>

                {/* ── Body ── */}
                <div className="overflow-y-auto flex-1 min-h-0">
                    {showGraph ? (
                        <CampaignGraph campaignStatsDetails={statsDetails} />
                    ) : (
                        <CampaignStats campaignStatsDetails={statsDetails} />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

